import React, { useState, useMemo } from 'react';
import { Product, ProductType } from '../types';
import Pagination from './Pagination';
import { ITEMS_PER_PAGE, PencilIcon, TrashIcon } from '../constants';

interface ProductTableProps {
  products: Product[];
  onModify: (product: Product) => void;
  onDelete: (product: Product) => void;
  onManageDefects?: (product: Product) => void;
}

const ProductTable: React.FC<ProductTableProps> = ({ products, onModify, onDelete, onManageDefects }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);

  // Check if any products are outfeed type to conditionally show display name columns
  const hasOutfeedProducts = useMemo(() => {
    return products.some(product => product.productType === ProductType.OUTFEED);
  }, [products]);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const lastPageIndex = firstPageIndex + ITEMS_PER_PAGE;
    return products.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, products]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (products.length === 0) {
    return <p className="text-slate-600 italic text-center py-8">No products found.</p>;
  }

  return (
    <div className="bg-white p-4 md:p-6 rounded-xl shadow-lg">
      <div className="overflow-x-auto">
        <table className={`w-full text-sm text-left text-slate-600 ${hasOutfeedProducts ? 'min-w-[900px]' : 'min-w-[700px]'}`}>
          <thead className="text-xs text-slate-500 uppercase bg-slate-50">
            <tr>
              <th scope="col" className="px-4 py-3">Product ID</th>
              <th scope="col" className="px-4 py-3">Product Name</th>
              <th scope="col" className="px-4 py-3 text-right">Average Piece Weight</th>
              {hasOutfeedProducts && <th scope="col" className="px-4 py-3">Outfeed Display Name</th>}
              {hasOutfeedProducts && <th scope="col" className="px-4 py-3">Yield Display Name</th>}
              <th scope="col" className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTableData.map((product) => (
              <tr key={product.id} className="bg-white border-b border-slate-200 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{product.productId}</td>
                <td className="px-4 py-3">{product.name}</td>
                <td className="px-4 py-3 text-right">{product.pieceWeight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                {hasOutfeedProducts && <td className="px-4 py-3">{product.outfeedDisplayName || '-'}</td>}
                {hasOutfeedProducts && <td className="px-4 py-3">{product.yieldDisplayName || '-'}</td>}
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center space-x-3">
                    <button
                      onClick={() => onModify(product)}
                      className="p-3 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded-lg border border-indigo-200 hover:border-indigo-300 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                      style={{ minHeight: '44px', minWidth: '44px' }}
                      title="Modify Product"
                      aria-label={`Modify product ${product.name}`}
                    >
                      <PencilIcon className="w-6 h-6" />
                    </button>
                    {product.productType === ProductType.OUTFEED && onManageDefects && (
                      <button
                        onClick={() => onManageDefects(product)}
                        className="p-3 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100 rounded-lg border border-yellow-200 hover:border-yellow-300 transition-all focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-1"
                        style={{ minHeight: '44px', minWidth: '44px' }}
                        title="Manage Defects"
                        aria-label={`Manage defects for ${product.name}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(product)}
                      className="p-3 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg border border-red-200 hover:border-red-300 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                      style={{ minHeight: '44px', minWidth: '44px' }}
                      title="Delete Product"
                      aria-label={`Delete product ${product.name}`}
                    >
                      <TrashIcon className="w-6 h-6" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default ProductTable;